/** @jsx jsx */
import { useFormikContext } from 'formik'
import isEmpty from 'lodash.isempty'
import { useContext, useEffect, useState } from 'react'
import { Box, Text, Flex, jsx } from 'theme-ui'
import { QuestionStepContext } from '../../context/QuestionStepContext'
import { getQuestionsBySectionId } from '../../utils/strategyBuilder'
import RadioInput from './form/FormInput/RadioInput'
import {
  IsQuestionRequiredError,
  QuestionVisible,
} from './form/InputValidationHelper'
import HiddenQuestion from './HiddenQuestion'
import LoadingSpinner from './LoadingSpinner'

const Matrix = () => {
  const { values, setFieldValue } = useFormikContext()
  const {
    rawTemplate,
    formikSchema,
    questionIsPercentGroupSection,
    nextButtonClicked,
  } = useContext(QuestionStepContext)
  const [questions, setQuestions] = useState()
  const [toggleQuestionData, setToggleQuestionData] = useState()
  const [isLoading, setIsLoading] = useState(true)

  const changeIsHide = ({ id, state = false }) => {
    const ID = `${id}-isHide`
    if (!formikSchema?._nodes.includes(ID)) return
    setFieldValue(ID, state)
  }

  const reCalculateIsHide = () => {
    questions?.forEach(question => {
      changeIsHide({
        id: toggleQuestionData.id,
        state: !QuestionVisible(question, values),
      })
    })
  }

  useEffect(() => {
    const questions = getQuestionsBySectionId({
      sectionId: '41',
      steps: 0,
      data: rawTemplate,
    })
    setQuestions(questions)
    const toggleQuestion = questions?.find(question => question.id === '102')
    setToggleQuestionData(toggleQuestion)
  }, [rawTemplate])

  useEffect(() => {
    if (!isEmpty(questions) && !isEmpty(toggleQuestionData)) {
      setIsLoading(false)
    }
  }, [questions, toggleQuestionData])

  if (isLoading) return <LoadingSpinner />

  return (
    <Flex
      sx={{
        flexDirection: 'column',
        gap: '50px',
        paddingBottom: '60px',
        mx: [3, 6],
      }}
    >
      <Flex sx={{ flexDirection: 'column', gap: '25px' }}>
        <Text
          data-automation-id={`question-section-question-text-${toggleQuestionData?.id}`}
          variant="mediumText"
          sx={{
            fontWeight: 'normal',
            color: 'black',
            fontSize: 3,
            mb: 12,
          }}
        >
          {toggleQuestionData?.text}
        </Text>
        <Flex sx={{ alignItems: 'center', gap: '60px' }}>
          {toggleQuestionData?.answers?.map(answer => (
            <RadioInput
              key={answer.id}
              values={values}
              question={toggleQuestionData}
              option={answer}
              onChange={reCalculateIsHide}
              sx={{
                '> .radio-input span': {
                  border:
                    IsQuestionRequiredError(
                      toggleQuestionData,
                      values,
                      questionIsPercentGroupSection
                    ) &&
                    nextButtonClicked &&
                    '2px solid red',
                },
              }}
            />
          ))}
        </Flex>
      </Flex>
      <Flex
        sx={{
          flexDirection: 'column',
          gap: '25px',
        }}
      >
        {QuestionVisible(questions?.[1], values) && !isEmpty(values) && (
          <Flex
            sx={{
              flexDirection: 'column',
              gap: '8px',
            }}
          >
            <Text
              sx={{
                fontWeight: 400,
                fontSize: '1.25rem',
              }}
            >
              Which of these retirement consulting services do you offer or are
              you interested in offering?
            </Text>
            <Text
              sx={{
                fontSize: '1.125rem',
                fontWeight: 400,
                color: 'darkGray',
              }}
            >
              Please, select one option in each row.
            </Text>
          </Flex>
        )}
        <Box as="table">
          {QuestionVisible(questions?.[1], values) && !isEmpty(values) && (
            <thead>
              <tr>
                <th />
                <th>
                  <Text
                    as="span"
                    sx={{
                      fontWeight: 400,
                      fontSize: '1rem',
                      textAlign: 'center',
                      color: 'darkGray',
                    }}
                    data-automation-id="question-section-already-doing"
                  >
                    Already Doing
                  </Text>
                </th>
                <th>
                  <Text
                    as="span"
                    sx={{
                      fontWeight: 400,
                      fontSize: '1rem',
                      textAlign: 'center',
                      color: 'darkGray',
                    }}
                    data-automation-id="question-section-interested-in-doing"
                  >
                    Interested in Doing
                  </Text>
                </th>
                <th>
                  <Text
                    as="span"
                    sx={{
                      fontWeight: 400,
                      fontSize: '1rem',
                      textAlign: 'center',
                      color: 'darkGray',
                    }}
                    data-automation-id="question-section-not-interested-in-offering"
                  >
                    Not Interested in Offering
                  </Text>
                </th>
              </tr>
            </thead>
          )}
          {!isEmpty(values) && (
            <tbody>
              {questions?.map(question => {
                return (
                  question.id !== '102' && (
                    <HiddenQuestion
                      key={question.id}
                      state={!QuestionVisible(question, values)}
                      question={question}
                      values={values}
                      isStrategyBuilderQuestion
                    >
                      <tr>
                        <Text
                          as="td"
                          sx={{
                            fontWeight: 400,
                            fontSize: '1rem',
                            padding: '15px 0',
                            color:
                              IsQuestionRequiredError(
                                question,
                                values,
                                questionIsPercentGroupSection
                              ) && nextButtonClicked
                                ? 'red'
                                : 'black',
                          }}
                          data-automation-id={`question-section-question-text-${question.id}`}
                        >
                          {question.subText}
                        </Text>

                        {question.answers.map(answer => (
                          <Text
                            key={answer.id}
                            as="td"
                            sx={{
                              fontWeight: 400,
                              fontSize: '1rem',
                              padding: '15px 0',
                              textAlign: 'center',
                              verticalAlign: 'middle',
                              position: 'relative',
                            }}
                          >
                            <RadioInput
                              values={values}
                              question={question}
                              option={answer}
                              hideLabel
                              centerRadio
                            />
                          </Text>
                        ))}
                      </tr>
                    </HiddenQuestion>
                  )
                )
              })}
            </tbody>
          )}
        </Box>
      </Flex>
    </Flex>
  )
}

Matrix.propTypes = {}

export default Matrix
